import { Component, ReactNode } from 'react'
import * as styles from 'components/features/pfm/TrendRank/TrendRank.css'
import * as capitalize from 'lodash/capitalize'
import { CategoryId } from 'interfaces/meta'
import { Timeframe } from 'interfaces/chart'
import { TimeframedPage } from 'interfaces/pages'
import { getMonthsCount } from 'helpers/dateTimeHelpers'
import { PlacementType } from 'interfaces/insights'
import MoneyflowDailyTrendContainer from 'components/features/pfm/LineChart/MoneyflowDailyTrendContainer'
import MoneyflowMonthlyTrendContainer from 'components/features/pfm/MonthlyBarChart/MoneyflowMonthlyTrendContainer'
import AccountsTrendModuleContainer from 'components/features/pfm/LineChart/AccountsTrendModuleContainer'
import GenericTrendModuleContainer from 'components/features/pfm/LineChart/GenericTrendModuleContainer'
import ModuleWrapper from 'components/features/pfm/ModuleWrapper/ModuleWrapper'
import AccountRankChartContainer from 'components/features/pfm/LineChart/AccountRankChartContainer'
import GenericRankChartContainer from 'components/features/pfm/LineChart/GenericRankChartContainer'
import MoneyflowRankChartContainer from 'components/features/pfm/LineChart/MoneyflowRankChartContainer'
import HorizontalSlider from 'components/atoms/HorizontalSlider/HorizontalSlider'
import { TrendRankMode } from 'interfaces/rank'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { isAccountPage, isGenericPage, isMoneyflowPage } from 'helpers/pageHelpers'
import { GenericTrendOwnProps } from 'interfaces/trend'
import TextButton from 'components/atoms/buttons/TextButton'
import cn from 'classnames'

export type TrendRankStateProps = {
  mode: TrendRankMode
  withComparison: boolean
}

export type TrendRankDispatchProps = {
  setMode: (mode: TrendRankMode) => void
}

export type TrendRankOwnProps = {
  type: Exclude<TimeframedPage, 'future'>
  timeframe: Timeframe
  categoryId?: CategoryId
}

export type TrendRankProps =
  & TrendRankStateProps
  & TrendRankDispatchProps
  & TrendRankOwnProps

class TrendRank extends Component<TrendRankProps> {
  render () {
    const { type, timeframe, categoryId, mode } = this.props

    let title: string | undefined
    const anchor: PlacementType = `${ type }_${ mode }` as PlacementType
    let trendChart: ReactNode | null = null
    let rankChart: ReactNode | null = null

    if (isMoneyflowPage(type)) {
      title = this.getTitleByType(type)

      const TrendContainer = getMonthsCount(timeframe) === 1
        ? MoneyflowDailyTrendContainer
        : MoneyflowMonthlyTrendContainer

      trendChart = (
        <TrendContainer
          type={ type }
          timeframe={ timeframe }
          categoryId={ categoryId as CategoryId }
        />
      )
      rankChart = categoryId === ALL_CATEGORY_ID
        ? (
          <MoneyflowRankChartContainer
            type={ type }
            timeframe={ timeframe }
            categoryId={ categoryId }
          />
        ) : null
    }

    if (isAccountPage(type)) {
      title = this.getTitleByType(type)

      trendChart = (
        <AccountsTrendModuleContainer
          type={ type }
          timeframe={ timeframe }
          categoryId={ categoryId as CategoryId }
        />
      )

      rankChart = categoryId === ALL_CATEGORY_ID
        ? (
          <AccountRankChartContainer
            type={ type }
            timeframe={ timeframe }
            categoryId={ categoryId }
          />
        ) : null
    }

    if (isGenericPage(type)) {
      const isCreditScore = type === 'creditscore'
      title = isCreditScore ? 'Credit Score Trend' : 'Net Worth Trend'

      const containerProps: GenericTrendOwnProps = {
        type,
        timeframe,
      }

      trendChart = <GenericTrendModuleContainer { ...containerProps } />
      rankChart = <GenericRankChartContainer { ...containerProps } />
    }

    const slides = [trendChart, rankChart]
    const currentSlide = mode === 'trend' ? 0 : 1

    return (
      <ModuleWrapper
        title={ title }
        action={ this.renderButton() }
        anchor={ anchor }
      >
        <div className={ styles.trendRankWrapper }>
          <div className={ styles.sliderWrapper }>
            <HorizontalSlider
              slides={ slides }
              current={ currentSlide }
            />
          </div>
        </div>
      </ModuleWrapper>
    )
  }

  renderButton () {
    const { mode, categoryId, withComparison } = this.props
    const isTrendMode = mode === 'trend'

    if (isTrendMode && categoryId && categoryId !== ALL_CATEGORY_ID) {
      return null
    }

    const buttonText = isTrendMode
      ? 'View your ranking'
      : 'View your trend'

    return (
      <div className={ cn(styles.slideButton, !withComparison && styles.hideRanking) }>
        <TextButton size='regular' onClick={ this.switchMode }>
          { buttonText }
        </TextButton>
      </div>
    )
  }

  private switchMode = () => {
    const { mode, setMode } = this.props
    const nextMode = mode === 'trend' ? 'rank' : 'trend'
    setMode(nextMode)
  }

  private getTitleByType (type: string) {
    return `${ capitalize(type) } Trend`
  }
}

export default TrendRank
